import React, { useEffect, useRef } from 'react';
import AOS from 'aos';
import '../styles/pages/Ballys.css';
import Navbar from '../components/layout/Navbar';

// Real production screenshots only. ChatGPT-generated placeholders
// removed — typography and layout do the heavy lifting where no real
// asset exists.
import PickemBanner from '../components/assets/Ballys/Pickem_Banner.png';
import MarketSpotlight from '../components/assets/Ballys/Market_Spotlight.png';
import SportsExplore from '../components/assets/Ballys/Sports_Explore.png';
import Top10Casino from '../components/assets/Ballys/Top10_Casino.png';
import VideoCarousel from '../components/assets/Ballys/Video_Carousel.png';
import FavoritesPreview from '../components/assets/Ballys/Favorites_Preview.png';
import FavoritesInGame from '../components/assets/Ballys/Favorite_ingame.png';
import FavoritesPage from '../components/assets/Ballys/Favorites_Page.png';
import BallyBucks from '../components/assets/Ballys/Bally_Bucks.png';
import CustomerInbox from '../components/assets/Ballys/Customer_Inbox.png';
import Benny from '../components/assets/Ballys/Benny.png';

// ─── Status badge ─────────────────────────────────────────────────
// Honest signal next to every project. Avoids the trap of presenting
// in-pipeline work as shipped (recruiters notice; they probe in
// interviews; trust dies fast when the page overclaims).
const STATUS = {
  SHIPPED: { label: 'Shipped', className: 'ballys-badge ballys-badge--shipped' },
  PIPELINE: { label: 'In pipeline', className: 'ballys-badge ballys-badge--pipeline' },
  ONGOING: { label: 'Ongoing', className: 'ballys-badge ballys-badge--ongoing' },
};

const Badge = ({ status }) => (
  <span className={status.className}>{status.label}</span>
);

// ─── Headline metrics ─────────────────────────────────────────────
// Only metrics directly grounded in the KB / verified by Chidrupa.
// "Significant rise" / "good footfall" type qualitatives stay
// qualitative — never inflated into invented numbers.
const HEADLINE_METRICS = [
  { metric: '36%',    label: 'Loyalty opt-in within ~2 months' },
  { metric: '290K',   label: 'Bally Bucks redeemed in first 2 months' },
  { metric: '~$3M',   label: "Wagering supported by Pick'em" },
];

const SUPPORTING_METRICS = [
  { metric: '~5K', label: "Weekly active Pick'em users" },
  { metric: '18',  label: 'Projects designed (shipped + in pipeline)' },
  { metric: '11',  label: 'Jurisdictions designed for' },
  { metric: '3',   label: 'Sports leagues (NFL, MLB, FIFA)' },
];

// ─── Sports projects (shipped) ────────────────────────────────────
const SPORTS_SHIPPED = [
  {
    name: 'Market Spotlight',
    desc: "Curated section that surfaces hot games at peak hours — March Madness, FIFA group stages, big finales. Backgrounds are color-matched to the playing teams so the carousel pops against the rest of the app.",
  },
  {
    name: 'Sports Explore',
    desc: "Instagram-style feed combining live streams (Bally Live), influencer content (WagerDot Games partnership), and contextual betting markets. Bet shown directly under the video that prompted it — the moment of intent is also the moment of action.",
  },
];

// ─── Casino projects (shipped) ────────────────────────────────────
const CASINO_SHIPPED = [
  {
    name: 'Top 10 Ranked',
    desc: "Netflix-style ranked spotlight on the casino home page — gold numbered overlays on game thumbnails. Once shipped, any title that landed in the Top 10 drove a lot of business; placement itself became the engagement driver.",
  },
  {
    name: 'Video Carousel',
    desc: "Animated carousel of provider game videos with min/max bets surfaced inline. Repurposed existing provider assets rather than commissioning new content — efficient, and movement on the home page is the cheapest attention-getter.",
  },
  {
    name: 'Favorites (multi-entry-point)',
    desc: "You can favorite a game from the preview modal, mid-launch, or from inside the discovery feed. Three entry points, one collection page. Favoriting is an impulse — meet it wherever it happens, not at one designated moment.",
  },
];

// ─── Compliance & Onboarding work ─────────────────────────────────
// State / jurisdiction names omitted on purpose — kept generic for
// regulatory and legal hygiene.
const COMPLIANCE_WORK = [
  { area: 'KYC flows',         desc: 'Identity-verification flows tailored to each jurisdiction\'s legal requirements — the first compliance launches I owned in the role.' },
  { area: 'Liveness & KBA',    desc: 'Liveness checks and Knowledge-Based Authentication question flows, redesigned per jurisdiction-specific identity verification standards.' },
  { area: 'Ongoing onboarding',desc: 'Registration flows across multiple jurisdictions — every new launch, every new requirement, comes back through this surface. Effectively the standing owner of NA regulatory UX.' },
];

// ─── In-pipeline / concept work ───────────────────────────────────
// Honestly framed: designed but not yet shipped. Recruiters trust
// honest "in pipeline" framing far more than vague past-tense
// descriptions of unreleased work.
const IN_PIPELINE = [
  {
    name: 'Ontario pre-registration launch',
    desc: "Pre-registration flow for the Ontario product launch with a cross-product redirect to Bally Play (the free-to-play app) on completion — pre-reg as a customer-acquisition mechanism for two products at once. Currently in development.",
  },
  {
    name: 'Parlay Winback',
    desc: "Original idea: a winning-leg leaderboard. Pivoted in a product leadership meeting once we realized we were rewarding people who already won — the riskiest behavior (parlays) was being punished by the very mechanic meant to encourage it. New design: even partial losses count toward leaderboard score, so a 4-leg parlay that goes 3-1 still adds 3 legs.",
  },
  {
    name: 'Parlay Friends',
    desc: "Collaborative parlay-building with friends. Admin creates a group, players join, a 15-minute timer kicks off when someone says 'let's build,' players add legs during the window, then make personal edits before submission. PII display constraints (names, identities) shaped the entire interaction.",
  },
  {
    name: 'Headless Jackpots',
    desc: "Opt-in casino jackpot system with four tiers (mini, minor, major, mega). Multiple players play simultaneously; once someone hits a random trigger, they take the pool. Working on tickers and the win for users.",
  },
  {
    name: 'New Game Highlight overlay',
    desc: "Session-start overlay promoting newly-added games — solves a real content-velocity problem, since 75-100 new games are added per week and passive discovery doesn't scale.",
  },
  {
    name: 'Casino Discovery Feed',
    desc: "Instagram-style discovery feed for casino — pattern reuse from Sports Explore. Game preview videos, promotions, live dealer games, all in one scrollable surface with favorites available inline.",
  },
  {
    name: 'VIP Zone',
    desc: "Conditional UI for high-roller accounts: black-and-gold theme imitating the VIP room of a land-based Bally's. Triggered when an account is flagged VIP — different theme, different quick links, different feature set, all coexisting with the standard app.",
  },
  {
    name: 'Benny → Zendesk live-agent handoff',
    desc: "Benny is Bally's existing in-app AI agent. I'm designing the next-phase handoff so Benny can escalate unresolved conversations to a live human on Zendesk and pass control back when the human is done. Includes a notification cue when the live agent replies (visitors go back to playing while waiting), and an archive that stores past conversations as cards within Benny.",
  },
];

const Ballys = () => {
  const progressRef = useRef(null);
  const progressMobileRef = useRef(null);

  useEffect(() => {
    AOS.init({ once: false, duration: 700, easing: 'ease-out-cubic' });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const percent = docHeight > 0 ? Math.max(0, Math.min(1, scrollTop / docHeight)) : 0;
      if (progressRef.current) {
        progressRef.current.style.height = `${percent * 100}%`;
      }
      if (progressMobileRef.current && window.innerWidth <= 700) {
        progressMobileRef.current.style.width = `${percent * 100}%`;
      }
    };

    window.scrollTo(0, 0);
    window.addEventListener('scroll', handleScroll);
    setTimeout(handleScroll, 100);
    AOS.refresh();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="ballys-page">
      <Navbar />

      <div className="ballys-progress-indicator">
        <div ref={progressRef} style={{ height: 0 }} />
      </div>
      <div className="ballys-progress-indicator-mobile">
        <div ref={progressMobileRef} style={{ width: 0 }} />
      </div>

      {/* ─── Hero (typographic, no image) ─── */}
      <header className="ballys-hero" data-aos="fade-up">
        <div className="ballys-hero__inner">
          <div className="ballys-hero__eyebrow">Case study · 2025–2026</div>
          <h1 className="ballys-hero__title">Bally's Interactive</h1>
          <p className="ballys-hero__subtitle">
            One designer. Two brands. Eleven jurisdictions. Eight months.
          </p>
          <div className="ballys-hero__scope">
            <span><strong>Bally Bet</strong> Sports &amp; Casino</span>
            <span className="dot" aria-hidden>·</span>
            <span><strong>Monopoly</strong> Sports &amp; Casino</span>
          </div>
        </div>
      </header>

      <div className="ballys-content">

        {/* ─── Overview ─── */}
        <section className="ballys-overview-section" data-aos="fade-up">
          <h2>Overview</h2>
          <p>
            I joined Bally's North American Interactive (NAI) as the sole product designer for the region during the spin-off from the UK-based parent design team. When the org separated in August 2025, I was the only designer on the ground — coordinating with a much larger UK team to learn their Figma conventions, design system, and engineering workflows, while simultaneously ramping on the betting domain itself and the products under it.
          </p>

          <div className="ballys-other-details-container">
            <div className="ballys-detail-item">
              <strong>ROLE</strong>
              <span>Associate Product Designer<br/><em className="ballys-detail-note">(sole NA designer scope; lead in product meetings)</em></span>
            </div>
            <div className="ballys-detail-item">
              <strong>DURATION</strong>
              <span>Aug 2025 – Apr 2026</span>
            </div>
            <div className="ballys-detail-item">
              <strong>SKILLS</strong>
              <span>UX Research, UX/UI, Design Systems, Design Ops</span>
            </div>
            <div className="ballys-detail-item">
              <strong>TOOLS</strong>
              <span>Figma, FullStory, GA, Jira, Claude MCP, Lottie</span>
            </div>
            <div className="ballys-detail-item">
              <strong>PLATFORMS</strong>
              <span>iOS, Android, Web (11 jurisdictions)</span>
            </div>
          </div>

          <p style={{ marginTop: '34px' }}>
            Over time the role grew well beyond IC. I sat in weekly product leadership meetings alongside the CPO, CTO, SVP of Engineering, and Director of Product Management — without a formal "lead" title, because no formal lead role existed for NA. As the team scaled, I informally managed two contract designers — owning the pipeline, briefing the work, holding the bar on quality. And I built and maintained the NA Figma architecture: core, core-player-experience, sports, and casino libraries; page templates with full-state coverage; and a Concepts → Production folder system with state tags so engineers consuming the published library never got broken by in-flight drafting.
          </p>
        </section>

        {/* ─── Impact at a glance ─── */}
        <section className="ballys-impact-section" data-aos="fade-up">
          <h2>Impact at a glance</h2>
          <div className="ballys-impact-grid ballys-impact-grid--hero">
            {HEADLINE_METRICS.map(m => (
              <div key={m.label} className="ballys-impact-card ballys-impact-card--hero">
                <div className="metric">{m.metric}</div>
                <div className="label">{m.label}</div>
              </div>
            ))}
          </div>
          <div className="ballys-impact-grid ballys-impact-grid--supporting">
            {SUPPORTING_METRICS.map(m => (
              <div key={m.label} className="ballys-impact-card">
                <div className="metric">{m.metric}</div>
                <div className="label">{m.label}</div>
              </div>
            ))}
          </div>
          <p className="ballys-impact-footnote">
            Other surfaces drove qualitative wins (Top 10 saw a "significant rise in plays," Sports Explore drove "good footfall") but I'm only quoting numbers I have data behind.
          </p>
        </section>

        {/* ─── Pick'em + Quick Pick decision ─── */}
        <section className="ballys-product-section" data-aos="fade-up">
          <div className="ballys-section-head">
            <h2>Pick'em — Flagship free-to-play</h2>
            <Badge status={STATUS.SHIPPED} />
          </div>

          <p>
            Pick six teams from the week's slate. Get all six right, you're entered into a draw. Designed as a "second chance" to win back money lost on regular bets — a single-screen game built to fit casino-style mechanics into a sports-betting app. I led design end-to-end across <strong>NFL → MLB → FIFA World Cup</strong>; the format scaled with subtle tweaks (MLB became a Friday–Sunday series; FIFA added a leaderboard layer for the round-based tournament structure).
          </p>

          <img src={PickemBanner} alt="Pick'em weekly giveaway" className="ballys-inline-img" />

          {/* Quick Pick decision callout — the strongest single anecdote
              in the case study. Full hypothesis → data → kill loop. */}
          <aside className="ballys-callout ballys-callout--decision" data-aos="fade-up">
            <div className="ballys-callout__eyebrow">Decision</div>
            <h3 className="ballys-callout__title">Quick Pick — when I killed my own idea</h3>
            <p>
              Picking six teams from a slate of 32 is a real cognitive ask. A lot of casino/betting users aren't there to think hard — they're there to participate in a game. So I shipped a Quick Pick button that randomized your six. Felt obviously smart.
            </p>
            <p>
              When we tracked usage in FullStory, only <strong>4–5%</strong> of users were tapping it. Not enough to justify the real estate on a single-page game. We removed it for the MLB launch.
            </p>
            <p className="ballys-callout__lesson">
              The lesson I keep coming back to: ship the version that lets you measure, not the version you're attached to.
            </p>
          </aside>
        </section>

        {/* ─── Sports — Engagement & Discovery ─── */}
        <section className="ballys-product-section" data-aos="fade-up">
          <div className="ballys-section-head">
            <h2>Sports — Engagement &amp; Discovery</h2>
            <Badge status={STATUS.SHIPPED} />
          </div>

          <p>
            I used FullStory session recordings, Google Analytics, and the Eilers Report (the industry's competitive benchmark of top sports/casino apps) to identify discovery gaps and friction in the existing sports surfaces.
          </p>

          <figure className="ballys-image-figure ballys-image-figure--phone">
            <img src={MarketSpotlight} alt="Bally Bet sports home — Top Games / Market Spotlight" />
            <figcaption>
              <strong>Market Spotlight</strong> — the live featured game sits at the top of the sports home with score, period, odds, and a live indicator surfaced inline. Below the spotlight, themed feature pills (Bally Boosts, In-Game, Explore, Parlay Zone) act as curated shortcuts into deeper discovery surfaces. The carousel rotates across peak hours — March Madness, Sunday football, FIFA group stages — with team-color-matched backgrounds so it pops against the rest of the app.
            </figcaption>
          </figure>

          <figure className="ballys-image-figure ballys-image-figure--phone ballys-image-figure--phone--reverse">
            <img src={SportsExplore} alt="Sports Explore — QuickPicks NHL influencer content with the connected market below" />
            <figcaption>
              <strong>Sports Explore</strong> — Instagram-style feed combining live streams (Bally Live), influencer content (WagerDot Games partnership), and contextual betting markets. The market connected to each video sits directly underneath, so the moment of intent is also the moment of action. Filter chips on top let users slice across influencer videos, live streams, promotions, and channels.
            </figcaption>
          </figure>

          <div className="ballys-feature-grid">
            <div className="ballys-feature-card ballys-feature-card--no-screenshot">
              <div className="ballys-feature-card__head">
                <h3>BetVision</h3>
                <Badge status={STATUS.SHIPPED} />
              </div>
              <p>
                Third-party in-video betting integration. My first project at Bally's. Bet slips and bet receipts had to render <em>on top of</em> a video player on mobile, with limited real estate, while surfacing offers — and stay within the third-party UX constraints.
              </p>
              <p className="ballys-feature-card__nda">No screenshot — third-party integration UI; conservatively kept off the page.</p>
            </div>
          </div>
        </section>

        {/* ─── Casino — Engagement & Discovery ─── */}
        <section className="ballys-product-section" data-aos="fade-up">
          <div className="ballys-section-head">
            <h2>Casino — Engagement &amp; Discovery</h2>
            <Badge status={STATUS.SHIPPED} />
          </div>

          <p>
            Casino was the biggest revenue driver at Bally's Interactive, and the home page was very flat. Several additions, summarized here, turned a static surface into an engagement layer:
          </p>

          {/* Top 10 origin story callout */}
          <aside className="ballys-callout ballys-callout--origin" data-aos="fade-up">
            <div className="ballys-callout__eyebrow">Origin story</div>
            <h3 className="ballys-callout__title">The Top 10 — picked up while the rest of us looked elsewhere</h3>
            <p>
              I noticed Netflix-style ranked spotlights working across competitors during a casual scan of the Eilers Report — nobody on our side was thinking about it. I picked it up, mocked the design, briefed the contractors, presented it in a product meeting, and it got pushed to the dev pipeline immediately.
            </p>
            <p>
              Once shipped, any game that landed in the Top 10 drove a lot of business. Placement itself became the mechanic — a hot seat that pulled traffic from the rest of the home page. The same numbered treatment got applied to live dealer games shortly after.
            </p>
          </aside>

          <figure className="ballys-image-figure ballys-image-figure--phone">
            <img src={Top10Casino} alt="Bally Casino home with Top 10 Ranked games" />
            <figcaption>
              <strong>Top 10 Ranked</strong> — gold numbered overlays on hot games. Above: the FIRE UP THE FUN promo carousel. Below this fold: Most Popular and New Games grids. Once the Top 10 shipped, placement on the list became the engagement mechanic — any game that landed there pulled significant traffic from the rest of the home page, week over week.
            </figcaption>
          </figure>

          <figure className="ballys-image-figure ballys-image-figure--phone ballys-image-figure--phone--reverse">
            <img src={VideoCarousel} alt="Casino Highlights video carousel and Top Ranked Live Dealer" />
            <figcaption>
              <strong>Video Carousel + Top Ranked Live Dealer</strong> — the Highlights row repurposes provider game videos with min/max bets surfaced inline. Movement is the cheapest attention-getter on a casino home page, and we got it for free by reusing assets the providers already had. The numbered ranking pattern carried over to live dealer games — same mechanic, different surface.
            </figcaption>
          </figure>

          <h3 className="ballys-subsection-title">Favorites — three entry points, one collection</h3>
          <p>
            Favoriting is impulsive. It happens when a player is mid-scroll and lands on something they want to come back to — not at a single designated "save game" moment. We built three entry points (preview modal, mid-launch chrome, discovery feed) all funneling into one Favorites destination.
          </p>

          <div className="ballys-image-pair">
            <figure className="ballys-image-figure">
              <img src={FavoritesPreview} alt="Game preview modal with heart favorite icon" />
              <figcaption>Heart in the game preview modal.</figcaption>
            </figure>
            <figure className="ballys-image-figure">
              <img src={FavoritesInGame} alt="In-game chrome with favorite icon visible during play" />
              <figcaption>In-game chrome — favorite mid-play, no friction.</figcaption>
            </figure>
            <figure className="ballys-image-figure">
              <img src={FavoritesPage} alt="Favorites destination page with saved games" />
              <figcaption>The destination — every saved game lands here.</figcaption>
            </figure>
          </div>

          <div className="ballys-feature-grid">
            <div className="ballys-feature-card ballys-feature-card--no-screenshot">
              <div className="ballys-feature-card__head">
                <h3>Casino Discovery Feed</h3>
                <Badge status={STATUS.PIPELINE} />
              </div>
              <p>
                Instagram-style discovery feed for casino — pattern reuse from Sports Explore. Game preview videos, promotions, live dealer games in one scrollable surface, with favorites available inline.
              </p>
            </div>
            <div className="ballys-feature-card ballys-feature-card--no-screenshot">
              <div className="ballys-feature-card__head">
                <h3>VIP Zone</h3>
                <Badge status={STATUS.PIPELINE} />
              </div>
              <p>
                Conditional UI for high-roller accounts — black-and-gold theme imitating the VIP room of a land-based Bally's. Triggered when an account is flagged VIP; coexists with the standard app.
              </p>
            </div>
            <div className="ballys-feature-card ballys-feature-card--no-screenshot">
              <div className="ballys-feature-card__head">
                <h3>New Game Highlight overlay</h3>
                <Badge status={STATUS.PIPELINE} />
              </div>
              <p>
                Session-start overlay promoting newly-added titles. The casino adds 75–100 new games per week — passive discovery doesn't scale at that velocity.
              </p>
            </div>
          </div>
        </section>

        {/* ─── Loyalty: Bally Bucks ─── */}
        <section className="ballys-product-section" data-aos="fade-up">
          <div className="ballys-section-head">
            <h2>Bally Bucks — Omnichannel Loyalty</h2>
            <Badge status={STATUS.SHIPPED} />
          </div>

          <p>
            A unified loyalty program where users earn points across <strong>casino, sports, and land-based casino spend</strong>, redeemable into a casino wallet, sports wallet, or bonus bets. The complexity sat in the cross-jurisdictional rules: some states don't allow expenditure earned in one jurisdiction to be redeemed in another, so the redemption flow had to be aware of state and behave differently per location.
          </p>

          <figure className="ballys-image-figure ballys-image-figure--phone">
            <img src={BallyBucks} alt="Bally Bucks redemption screen with tier progress and redeemable balance" />
            <figcaption>
              <strong>Redemption screen</strong> — tier progress (Pro → Star, "Next at 4,000"), current redeemable balance, and the math gap to the minimum redemption threshold ("99 Bally Bucks Required to reach 100") all surfaced on a single screen. The three-way redemption split (sports wallet, casino wallet, bonus bets) had to thread cross-jurisdictional rules without burdening users with the regulation.
            </figcaption>
          </figure>

          <aside className="ballys-callout ballys-callout--metric">
            <div className="ballys-callout__eyebrow">Outcome</div>
            <div className="ballys-callout__big">36% opt-in within ~2 months</div>
            <p>
              Loyalty was opt-in by design — a deliberate choice that affects regulatory posture, user trust, and measurable engagement. The 36% take-rate within a couple of months was a strong signal that the discoverability work (limited nav-bar real estate; tier progress visible at a glance) was paying off.
            </p>
          </aside>
        </section>

        {/* ─── Customer Communications ─── */}
        <section className="ballys-product-section" data-aos="fade-up">
          <div className="ballys-section-head">
            <h2>Customer Communications</h2>
          </div>

          <h3 className="ballys-subsection-title">Customer Inbox <Badge status={STATUS.SHIPPED} /></h3>
          <p>
            An in-app inbox unifying promotions, customer service threads, and account updates. Built off a real research insight: account-relevant emails get filtered as promotional in Gmail and never read, so important messages have to live somewhere visible inside the app where users actually are.
          </p>

          <figure className="ballys-image-figure ballys-image-figure--phone">
            <img src={CustomerInbox} alt="Customer Inbox with promo messages and category filters" />
            <figcaption>
              <strong>Inbox</strong> — All / 📬 / Promo filter chips up top, sport-themed message cards (Finale Boost, Series Finale, TEX vs DET, Serve &amp; Boost, Final Day Live) with timestamps and read state, and a 31-day retention rule visible at the bottom. The user-research insight: account-relevant emails get filtered as promotional in Gmail and never read, so important messages have to live somewhere visible inside the app where users actually are.
            </figcaption>
          </figure>

          <h3 className="ballys-subsection-title">Benny → Zendesk live-agent handoff <Badge status={STATUS.PIPELINE} /></h3>

          <figure className="ballys-image-figure ballys-image-figure--phone ballys-image-figure--phone--reverse">
            <img src={Benny} alt="Benny — Bally's existing in-app AI agent" />
            <figcaption>
              <strong>Context: the existing Benny agent.</strong> Benny is Bally's pre-existing in-app AI agent — I didn't design or build it. My work is the next-phase <strong>Zendesk integration</strong> — designing the failover so Benny can hand a stuck conversation to a live human on Zendesk, signal new live-agent replies with an urgency cue (so users can keep playing while waiting), and route the conversation back to Benny once the human wraps up. Completed conversations archive as cards within Benny so visitors can revisit. <em>In pipeline.</em>
            </figcaption>
          </figure>

          <p>
            The interesting design problem is the in-between state. While the live agent is typing, users go back to playing — they're not staring at the chat. The notification cue needs to pull them back without disrupting whatever else they're doing. Most chat agents lose state on handoff; this one preserves it.
          </p>
        </section>

        {/* ─── Compliance & Onboarding ─── */}
        <section className="ballys-product-section" data-aos="fade-up">
          <div className="ballys-section-head">
            <h2>Compliance &amp; Onboarding</h2>
            <Badge status={STATUS.ONGOING} />
          </div>
          <p>
            The least visible work and arguably the highest-leverage. Every regulated launch brings its own KYC, liveness, and KBA requirements, and every new requirement reshapes the registration flow. I'm effectively the ongoing owner of NA regulatory UX — every new launch comes back through this surface.
          </p>
          <p className="ballys-compliance-note">
            No screenshots and no jurisdiction names — registration and identity flows are conservatively kept off the page given regulatory and security sensitivity.
          </p>
          <div className="ballys-compliance-grid">
            {COMPLIANCE_WORK.map(w => (
              <div key={w.area} className="ballys-compliance-row">
                <div className="ballys-compliance-row__label">{w.area}</div>
                <div className="ballys-compliance-row__desc">{w.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── In-pipeline / Concept work ─── */}
        <section className="ballys-pipeline-section" data-aos="fade-up">
          <div className="ballys-section-head">
            <h2>In Pipeline — Designed, not yet shipped</h2>
            <Badge status={STATUS.PIPELINE} />
          </div>
          <p>
            Work I've designed but that hasn't shipped yet. Included for fullness — but I'd rather under-claim a feature than overclaim it.
          </p>
          <div className="ballys-pipeline-grid">
            {IN_PIPELINE.map(p => (
              <div key={p.name} className="ballys-pipeline-card">
                <h3>{p.name}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ─── Design Operations & Leadership ─── */}
        <section className="ballys-leadership-section" data-aos="fade-up">
          <h2>Design Operations &amp; Leadership</h2>
          <p>
            Beyond shipping product work, I built the infrastructure that let other designers plug in without breaking what engineers were already consuming. The Figma architecture is a hierarchy of <strong>components → page templates → user journeys</strong>, with a Concepts → Production folder split that protects published libraries from in-flight drafting.
          </p>

          {/* Custom CSS Figma architecture diagram — typographic,
              not a fake screenshot. Communicates systems thinking
              without revealing IP. */}
          <div className="ballys-figma-diagram" aria-label="Figma architecture: components feed into page templates feed into user journeys">
            <div className="ballys-figma-row ballys-figma-row--components">
              <div className="ballys-figma-row__label">Components</div>
              <div className="ballys-figma-row__items">
                <span>Core</span>
                <span>Core Player Experience</span>
                <span>Sports</span>
                <span>Casino</span>
              </div>
            </div>
            <div className="ballys-figma-arrow" aria-hidden>↓</div>
            <div className="ballys-figma-row ballys-figma-row--templates">
              <div className="ballys-figma-row__label">Page templates</div>
              <div className="ballys-figma-row__items">
                <span>Sports</span>
                <span>Casino</span>
                <span>Promotions &amp; Rewards</span>
                <span>Core Player Experience</span>
              </div>
            </div>
            <div className="ballys-figma-arrow" aria-hidden>↓</div>
            <div className="ballys-figma-row ballys-figma-row--journeys">
              <div className="ballys-figma-row__label">User journeys</div>
              <div className="ballys-figma-row__items">
                <span>Onboarding</span>
                <span>Wager flow</span>
                <span>Loyalty redemption</span>
                <span>Compliance / KYC</span>
              </div>
            </div>
            <div className="ballys-figma-meta">
              <div><strong>Concepts</strong> folder for in-flight drafting</div>
              <div><strong>Production</strong> folder for engineer-consumed published libraries</div>
              <div>State tags on every file: <em>concept · in-dev · production · archived</em></div>
            </div>
          </div>

          <p>
            On the leadership side: I sat in weekly product meetings with the CPO, CTO, SVP of Engineering, and Director of Product Management. As contractors joined the team, I owned their pipeline — gathering requirements, briefing the work, holding the bar on quality. No formal manager title; the responsibility was the title.
          </p>
        </section>

        {/* ─── Reflections ─── */}
        <section className="ballys-reflections-section" data-aos="fade-up">
          <h2>What I take with me</h2>

          <div className="ballys-reflection">
            <h3>Regulated design is constraint design.</h3>
            <p>
              Every state has its own KYC, liveness, KBA, and registration rules — and recruiters at any fintech, healthcare, or regulated industry care about this far more than at first glance. The work isn't flashy. The skill is translating legal posture into UX without burdening users with friction or compromising compliance.
            </p>
          </div>

          <div className="ballys-reflection">
            <h3>Ship the version you can measure.</h3>
            <p>
              Quick Pick was the lesson and the receipt. The "obviously good" idea only stays good if your users actually use it; the only way to know is to instrument it and accept what the data tells you, even when it kills your favorite feature.
            </p>
          </div>

          <div className="ballys-reflection">
            <h3>Operating systems beat heroics.</h3>
            <p>
              The Figma architecture and the Concepts/Production split mattered more than any single screen I shipped. As soon as contractors joined, the design system was the difference between contributing safely and breaking what engineers had already built. Build the operating system; don't be the operating system.
            </p>
          </div>
        </section>

      </div>
    </div>
  );
};

export default Ballys;
